import { A2pBrand, A2pBrandPayload } from '@/apis/business/setting/phoneNumbers/types';
import { STATE_OPTIONS } from './hostSmsConstants';

export type A2pBrandFormValues = {
  display_name: string;
  entity_type: string;
  company_name: string;
  ein: string;
  phone: string;
  email: string;
  business_contact_email: string;
  street: string;
  city: string;
  country: string;
  state: string;
  postal_code: string;
  vertical: string;
  website: string;
};

// Sole proprietor brands need a carrier mobile verification step that is not supported yet.
export const ENTITY_TYPE_OPTIONS = [
  { value: '', label: 'Select' },
  { value: 'PRIVATE_PROFIT', label: 'Privately Held Company' },
  { value: 'PUBLIC_PROFIT', label: 'Publicly Traded Company' },
  { value: 'NON_PROFIT', label: 'Nonprofit Organisation' },
  { value: 'GOVERNMENT', label: 'Government' }
];

export const VERTICAL_OPTIONS = [
  { value: '', label: 'Select' },
  { value: 'AGRICULTURE', label: 'Agriculture' },
  { value: 'COMMUNICATION', label: 'Communication' },
  { value: 'CONSTRUCTION', label: 'Construction' },
  { value: 'EDUCATION', label: 'Education' },
  { value: 'ENERGY', label: 'Energy' },
  { value: 'ENTERTAINMENT', label: 'Entertainment' },
  { value: 'FINANCIAL', label: 'Financial' },
  { value: 'GAMBLING', label: 'Gambling' },
  { value: 'GOVERNMENT', label: 'Government' },
  { value: 'HEALTHCARE', label: 'Healthcare' },
  { value: 'HOSPITALITY', label: 'Hospitality' },
  { value: 'HUMAN_RESOURCES', label: 'Human Resources' },
  { value: 'INSURANCE', label: 'Insurance' },
  { value: 'LEGAL', label: 'Legal' },
  { value: 'MANUFACTURING', label: 'Manufacturing' },
  { value: 'NGO', label: 'NGO' },
  { value: 'POLITICAL', label: 'Political' },
  { value: 'POSTAL', label: 'Postal' },
  { value: 'REAL_ESTATE', label: 'Real Estate' },
  { value: 'RETAIL', label: 'Retail' },
  { value: 'TECHNOLOGY', label: 'Technology' },
  { value: 'TRANSPORTATION', label: 'Transportation' }
];

export const EMPTY_BRAND_FORM: A2pBrandFormValues = {
  display_name: '',
  entity_type: '',
  company_name: '',
  ein: '',
  phone: '',
  email: '',
  business_contact_email: '',
  street: '',
  city: '',
  country: '',
  state: '',
  postal_code: '',
  vertical: '',
  website: ''
};

export const DEFAULT_BRAND_RELATIONSHIP = 'BASIC_ACCOUNT';

export const A2P_POLL_INTERVAL_MS = 15000;

export const BRAND_REJECTED_FALLBACK =
  'The carrier did not approve your brand details. Review the information below, correct anything that does not match your official business records, and resubmit.';

const US_ALIASES = ['us', 'usa', 'united states', 'united states of america'];
const CA_ALIASES = ['ca', 'canada'];

const countryCode = (country: string) => {
  const normalized = country.trim().toLowerCase();
  if (US_ALIASES.includes(normalized)) return 'US';
  if (CA_ALIASES.includes(normalized)) return 'CA';
  return country.trim().toUpperCase();
};

export const stateOptionsForCountry = (country?: string) => {
  const normalized = (country || '').trim().toLowerCase();
  if (US_ALIASES.includes(normalized)) return STATE_OPTIONS.US;
  if (CA_ALIASES.includes(normalized)) return STATE_OPTIONS.CA;
  return [...STATE_OPTIONS.CA, ...STATE_OPTIONS.US];
};

export const brandToFormValues = (brand: A2pBrand): A2pBrandFormValues => {
  const details = (brand.details || {}) as Record<string, unknown>;
  const detail = (key: keyof A2pBrandFormValues) => (typeof details[key] === 'string' ? (details[key] as string) : '');
  const values = Object.fromEntries(
    (Object.keys(EMPTY_BRAND_FORM) as (keyof A2pBrandFormValues)[]).map((key) => [key, detail(key)])
  ) as A2pBrandFormValues;

  return {
    ...values,
    display_name: brand.display_name || values.display_name,
    entity_type: brand.entity_type || values.entity_type,
    company_name: brand.company_name || values.company_name,
    ein: brand.ein || values.ein
  };
};

export const brandFormToPayload = (values: A2pBrandFormValues): A2pBrandPayload => {
  const trimmed = Object.fromEntries(
    Object.entries(values).map(([key, value]) => [key, String(value ?? '').trim()])
  ) as A2pBrandFormValues;

  return {
    ...trimmed,
    country: countryCode(trimmed.country),
    business_contact_email: trimmed.business_contact_email || undefined,
    brand_relationship: DEFAULT_BRAND_RELATIONSHIP,
    is_main: true
  };
};
